import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "./AppContext";
import type { LedgerQuery, LedgerView } from "./api";

export interface LedgerState {
  /** What was asked for, resolved from the URL. */
  query: LedgerQuery;
  view: LedgerView | null;
  /** True until the first answer, so the page can tell "nothing yet" from "not yet". */
  loading: boolean;
  problem: string | null;
  /** Set when the URL carries `?insight=`, arrived at from a belief's provenance (UI-7). */
  focus: string | null;
  refresh: () => void;
}

const LedgerContext = createContext<LedgerState | null>(null);

/**
 * UI-9 — the ledger's one read, held where both the page and the navigator panel can see it.
 *
 * Inside the router rather than beside the other app-wide reads in `AppContext`, because the
 * query comes from the URL and `AppProvider` is mounted above `BrowserRouter`. That placement
 * is what makes the milestone's requirement structural rather than remembered: the panel's
 * counts and the page's rows are literally the same object, so "mirror the same filters in the
 * navigator panel and the page so the two never disagree" is not something a later edit can
 * quietly break.
 *
 * It fetches only while the Insights destination is open. Every other screen pays nothing for
 * a provider that is always mounted.
 */
export function LedgerProvider({ children }: { children: React.ReactNode }) {
  const { api } = useApp();
  const { pathname, search } = useLocation();

  const active = pathname.startsWith("/insights");

  const [view, setView] = useState<LedgerView | null>(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  const { query, focus } = useMemo(() => resolve(search), [search]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    setLoading(true);

    void api
      .getLedger(query)
      .then((answer) => {
        if (cancelled) return;
        setView(answer);
        setProblem(null);
      })
      .catch(() => {
        if (!cancelled) setProblem("The insights ledger could not be read.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, active, query, reloads]);

  const value = useMemo<LedgerState>(
    () => ({ query, view, loading, problem, focus, refresh: () => setReloads((n) => n + 1) }),
    [query, view, loading, problem, focus],
  );

  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>;
}

/**
 * The filters, read out of the URL so a filtered ledger is a link somebody can keep.
 *
 * `?insight=` is the one that changes the others. It is how a belief's provenance reaches the
 * row for the insight behind it (UI-7), and that insight may well have been read months ago
 * and later discarded — so unless the link says otherwise, arriving that way widens the
 * horizon and the state to everything rather than landing on an empty list.
 */
function resolve(search: string): { query: LedgerQuery; focus: string | null } {
  const params = new URLSearchParams(search);
  const focus = params.get("insight");

  return {
    query: {
      horizon: params.get("horizon") ?? (focus ? "all" : "7d"),
      state: params.get("state") ?? (focus ? "all" : "all"),
      verdict: params.get("verdict"),
      source: params.get("source"),
      text: params.get("q"),
    },
    focus,
  };
}

/**
 * Throws outside the provider rather than returning null, for the same reason `useApp` does:
 * a missing provider is a wiring mistake that should fail loudly in development rather than
 * render a screen with no numbers on it.
 */
export function useLedger(): LedgerState {
  const value = useContext(LedgerContext);
  if (!value) throw new Error("useLedger was called outside a LedgerProvider.");

  return value;
}
